// src/contexts/MessagesContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef
} from "react";
import { useUser } from "./UserContext";

/* ===================== API Helpers ===================== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "";

const CLEAN_API_BASE = (API_BASE || "").replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const BASE = CLEAN_API_BASE || FALLBACK_BASE;

const TOKEN_STORAGE_KEYS = ["token", "jwt", "JWT", "accessToken", "authToken"];

const getStoredToken = () => {
  try {
    for (const k of TOKEN_STORAGE_KEYS) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
  } catch (_) {}
  return null;
};

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function authFetch(path, { method = "GET", body } = {}) {
  const token = getStoredToken();
  if (!token) throw new Error("נדרשת התחברות (חסר טוקן)");

  const res = await fetch(`${BASE}${path.startsWith("/") ? path : `/${path}`}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
  });

  const data = await parseJSONSafely(res);
  if (!res.ok || data?.ok === false) {
    const msg = data?.error || data?.message || `שגיאת שרת (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    e.payload = data;
    throw e;
  }
  return data ?? {};
}

/* ===================== Context ===================== */
const MessagesContext = createContext(null);

export const MessagesProvider = ({ children }) => {
  const { role, showAlert } = useUser();
  const isAdmin = role === "admin";

  // server paging (ONE page in state)
  const [page, setPage] = useState(1);
  const [limit] = useState(4); // ✅ matches your UI page size
  const [status, setStatus] = useState("all"); // all | open | replied
  const [q, setQ] = useState(""); // local search (within loaded page)

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0); // pagination total (from API)

  const [selected, setSelected] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [showReplyPanel, setShowReplyPanel] = useState(false);

  // ✅ Global counters (for ALL DB messages) + optimistic local updates
  const [meta, setMeta] = useState({
    countsGlobal: null, // { total, unread, unreplied, replied }
  });

  const fetchedCountsOnceRef = useRef(false);

  const loadGlobalCountsOnce = useCallback(async () => {
    if (!isAdmin) return;
    if (fetchedCountsOnceRef.current) return; // ✅ only once
    fetchedCountsOnceRef.current = true;

    try {
      // ✅ BEST: create a dedicated endpoint for counts
      // Example suggested backend route:
      // GET /api/admin/contact-messages/counts  -> { ok:true, counts:{ total, unread, unreplied, replied } }

      const data = await authFetch("/api/admin/contact-messages/counts");
      const g = data?.counts || data?.counts?.all || null;

      if (g && typeof g === "object") {
        setMeta((prev) => ({ ...prev, countsGlobal: g }));
      }
    } catch (e) {
      // ✅ fallback: if you don't have /counts endpoint yet,
      // we can try to read counts from list API ONCE (page 1 minimal limit)
      try {
        const params = new URLSearchParams({ page: "1", limit: "1" });
        const data2 = await authFetch(`/api/admin/contact-messages?${params.toString()}`);
        const g2 = data2?.counts?.all || data2?.counts || null;

        if (g2 && typeof g2 === "object") {
          setMeta((prev) => ({ ...prev, countsGlobal: g2 }));
        }
      } catch (_) {
        // if both fail, counts will fallback to page counts in your `counts` memo (optional)
      }
    }
  }, [isAdmin]);

  const patchGlobalCounts = useCallback((mutate) => {
    setMeta((prev) => {
      if (!prev.countsGlobal) return prev;
      const next = { ...prev.countsGlobal };
      mutate(next);
      return { ...prev, countsGlobal: next };
    });
  }, []);

  const buildListParams = useCallback(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    if (status === "open") params.set("unanswered", "1");
    if (status === "replied") params.set("unanswered", "0");

    return params;
  }, [page, limit, status]);

  const loadList = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    try {
      const params = buildListParams();

      let data;
      try {
        data = await authFetch(`/api/admin/contact-messages?${params.toString()}`);
      } catch (_) {
        data = await authFetch(`/api/users/contact/messages?${params.toString()}`);
      }

      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      setItems(list);
      setTotal(Number(data?.total ?? 0));

      const g = data?.counts?.all || data?.counts || null;
      if (g && typeof g === "object") {
        setMeta((prev) => {
          if (prev.countsGlobal) return prev; // ✅ don't overwrite on page change
          return { ...prev, countsGlobal: g };
        });
      }
    } catch (e) {
      showAlert(e?.message || "שגיאה בטעינת הודעות", "error");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, buildListParams, showAlert]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await loadList();
    setTimeout(() => setRefreshing(false), 400);
  }, [loadList]);

  // load on init + whenever paging/status changes
  useEffect(() => {
    if (!isAdmin) return;
    loadList();
    setSelected(null);
    setReplyText("");
    setShowReplyPanel(false);
  }, [isAdmin, page, status, loadList]);

  useEffect(() => {
    if (!isAdmin) return;
    loadGlobalCountsOnce(); // ✅ fetch once only
  }, [isAdmin, loadGlobalCountsOnce]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return items;

    return (items || []).filter((m) => {
      const hay = [
        m.first_name,
        m.last_name,
        m.email,
        m.phone,
        m.subject,
        m.message,
        m.admin_reply,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(qq);
    });
  }, [items, q]);

  const counts = useMemo(() => {
    const g = meta.countsGlobal;

    // fallback to current page if backend doesn't send global counts
    const pageUnread = (items || []).filter((m) => String(m.is_read ?? 0) === "0").length;
    const pageUnreplied = (items || []).filter(
      (m) => !(m.admin_reply && String(m.admin_reply).trim() !== "")
    ).length;
    const pageReplied = (items || []).length - pageUnreplied;

    return {
      globalTotal: g?.total ?? total ?? 0,
      globalUnread: g?.unread ?? pageUnread,
      globalUnreplied: g?.unreplied ?? pageUnreplied,
      globalReplied: g?.replied ?? pageReplied,
    };
  }, [meta.countsGlobal, items, total]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / limit)),
    [total, limit]
  );

  const goNext = useCallback(
    () => setPage((p) => Math.min(totalPages, p + 1)),
    [totalPages]
  );
  const goPrev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);

  const clearSelected = useCallback(() => {
    setSelected(null);
    setReplyText("");
    setShowReplyPanel(false);
  }, []);

  const canReply = useMemo(() => {
    if (!selected) return false;
    const already = !!(selected.admin_reply && String(selected.admin_reply).trim() !== "");
    return !already;
  }, [selected]);

  
  /**
   * ✅ openMessage WITHOUT extra GET:
   * - uses already-loaded message object from the list
   * - no "fetch details" call
   * - optionally calls "mark read" endpoint ONLY if backend needs it
   */
  const openMessage = useCallback(
    async (input) => {
      const id = typeof input === "object" ? input?.id : input;

      const local =
        (typeof input === "object" && input) ||
        (items || []).find((x) => String(x.id) === String(id)) ||
        null;

      if (!local) return;

      const wasUnreadLocal = String(local.is_read ?? 0) === "0";
      const nowIso = new Date().toISOString();

      const patchedLocal = {
        ...local,
        is_read: 1,
        read_at: local.read_at || nowIso,
      };

      setSelected(patchedLocal);
      setReplyText(patchedLocal?.admin_reply || "");
      setShowReplyPanel(false);

      setItems((prev) =>
        (prev || []).map((m) =>
          String(m.id) === String(patchedLocal.id) ? { ...m, ...patchedLocal } : m
        )
      );

      if (wasUnreadLocal) {
        patchGlobalCounts((g) => {
          g.unread = Math.max(0, (g.unread ?? 0) - 1);
        });

        // ✅ persist to DB (no re-fetch)
        try {
          await authFetch(
            `/api/admin/contact-messages/${encodeURIComponent(patchedLocal.id)}/read`,
            { method: "PATCH" }
          );
        } catch (e) {
          // optional: revert UI if you want
        }
      }
    },
    [items, patchGlobalCounts]
  );

  const sendReply = useCallback(async () => {
    if (!selected) return;

    const txt = replyText.trim();
    if (txt.length < 2) {
      showAlert("נא לכתוב תשובה", "error");
      return;
    }
    if (!canReply) {
      showAlert("כבר נשלחה תשובה להודעה זו", "error");
      return;
    }

    setSendingReply(true);

    const prevItems = items;
    const prevSelected = selected;
    const prevCounts = meta.countsGlobal ? { ...meta.countsGlobal } : null;
    const prevShowReplyPanel = showReplyPanel;

    const wasUnreplied = !(selected.admin_reply && String(selected.admin_reply).trim() !== "");
    const wasUnread = String(selected.is_read ?? 0) === "0";

    const optimistic = {
      ...selected,
      admin_reply: txt,
      replied_at: new Date().toISOString(),
      is_read: 1,
      read_at: selected.read_at || new Date().toISOString(),
    };

    setSelected(optimistic);
    setItems((prev) =>
      (prev || []).map((m) =>
        String(m.id) === String(optimistic.id) ? { ...m, ...optimistic } : m
      )
    );
    setShowReplyPanel(false);

    if (wasUnreplied) {
      patchGlobalCounts((g) => {
        g.unreplied = Math.max(0, (g.unreplied ?? 0) - 1);
        g.replied = (g.replied ?? 0) + 1;
      });
    }
    if (wasUnread) {
      patchGlobalCounts((g) => {
        g.unread = Math.max(0, (g.unread ?? 0) - 1);
      });
    }

    try {
      await authFetch(`/api/admin/contact-messages/${encodeURIComponent(selected.id)}/reply`, {
        method: "PATCH",
        body: { reply: txt },
      });

      showAlert("התשובה נשלחה בהצלחה! 🎉", "success");
    } catch (e) {
      if (prevCounts) setMeta((p) => ({ ...p, countsGlobal: prevCounts }));
      setItems(prevItems);
      setSelected(prevSelected);
      setShowReplyPanel(prevShowReplyPanel);

      showAlert(e?.message || "שגיאה בשליחת תשובה", "error");
    } finally {
      setSendingReply(false);
    }
  }, [
    selected,
    replyText,
    canReply,
    items,
    meta.countsGlobal,
    showReplyPanel,
    patchGlobalCounts,
    showAlert,
  ]);

  const value = useMemo(
    () => ({
      isAdmin,

      page,
      limit,
      status,
      q,
      items,
      filtered,
      total,
      totalPages,
      counts,

      loading,
      refreshing,
      detailsLoading,
      sendingReply,

      selected,
      clearSelected,
      replyText,
      showReplyPanel,
      canReply,

      setPage,
      setStatus,
      setQ,
      refresh,
      openMessage,
      setReplyText,
      setShowReplyPanel,
      sendReply,
      goNext,
      goPrev,
    }),
    [
      isAdmin,
      page,
      limit,
      status,
      q,
      items,
      filtered,
      total,
      totalPages,
      counts,
      loading,
      refreshing,
      detailsLoading,
      sendingReply,
      selected,
      clearSelected,
      replyText,
      showReplyPanel,
      canReply,
      refresh,
      openMessage,
      sendReply,
      goNext,
      goPrev,
    ]
  );

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
};

export const useMessages = () => {
  const ctx = useContext(MessagesContext);
  if (!ctx) throw new Error("useMessages must be used within MessagesProvider");
  return ctx;
};
